import { Component, OnInit } from "@angular/core";
import {
  faDownload,
  faEdit,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "src/app/interfaces/form-field.model";
import { CustomerSearchService } from "src/app/services/customer/customer-search.service";
import { NotifierService } from "angular-notifier";

@Component({
  selector: "app-customer-search",
  templateUrl: "./customer-search.component.html",
  styleUrls: ["./customer-search.component.css"],
})
export class CustomerSearchComponent implements OnInit {
  // ─── Icons ───────────────────────────────────────────────
  faEdit = faEdit;
  faDownload = faDownload;
  faPrint = faPrint;

  // ─── Data ────────────────────────────────────────────────
  fullModelsData: any[] = [];
  fullActiveUsers: any[] = [];
  tableData: any[] = [];
  selectedModelId: string = "";

  // ─── UI State ────────────────────────────────────────────
  lockUpdate = true;
  lockPrint = true;

  // ─── Forms ───────────────────────────────────────────────
  modelForm: FormField[] = [];
  searchFields: FormField[] = [
    {
      type: "search",
      id: "IcNo",
      label: "NRIC No/RCB No/Passport No",
      required: true,
      searchEvent: true,
    },
    {
      type: "search",
      id: "Name",
      label: "Name as in NRIC/Passport",
      required: true,
      searchEvent: true,
    },
    {
      type: "select",
      id: "Title",
      label: "Title",
      required: true,
      options: [
        { display: "MR", value: "MR" },
        { display: "MRS", value: "MRS" },
        { display: "MISS", value: "MISS" },
        { display: "M/S", value: "M/S" },
        { display: "MDM", value: "MDM" },
        { display: "DR", value: "DR" },
        { display: "MS", value: "MS" },
      ],
    },
    {
      type: "text",
      id: "AddressBlock",
      label: "Block",
      required: true,
    },
    {
      type: "text",
      id: "AddressRoad",
      label: "Road",
      required: true,
    },
    { type: "text", id: "AddressUnit", label: "Unit" },
    {
      type: "search",
      id: "PostalCode",
      label: "Postal Code",
      required: true,
      searchEvent: true,
    },
    {
      type: "date",
      id: "Dob",
      label: "Date of birth (dd/mm/yyyy)",
      required: true,
    },
    {
      type: "search",
      id: "Hp",
      label: "Mobile Number",
      required: true,
      searchEvent: true,
    },
    { type: "text", id: "ResidenceNo", label: "Home tel number" },
    { type: "text", id: "OfficeNo", label: "Office Tel Number" },
    {
      type: "text",
      id: "Occupation",
      label: "Occupation",
      required: true,
    },
    { type: "email", id: "Email", label: "Email", required: true },
    { type: "text", id: "ModelShortDesc", label: "Model" },
    {
      type: "search",
      id: "RegisterNumber",
      label: "Car Registration Number",
      searchEvent: true,
    },
    {
      type: "date",
      id: "REGISTRATIONDATE",
      label: "Car Registration Date (dd/mm/yyyy)",
    },
    {
      type: "select",
      id: "SalemansCode",
      label: "Sales Consultant",
      options: [],
    },
    { type: "text", id: "UserOwner", label: "", show: false },
    { type: "text", id: "ContractNo", label: "", show: false },
    { type: "text", id: "CurrentCarModel", label: "", show: false },
    { type: "text", id: "CurrentRegDate", label: "", show: false },
    { type: "text", id: "ModelInterested", label: "", show: false },
    {
      type: "text",
      id: "ModelInterestedOthers",
      label: "",
      show: false,
    },
    { type: "text", id: "REGISTERNO", label: "", show: false },
    { type: "text", id: "user_owner", label: "", show: false },
    { type: "text", id: "SalemansID", label: "", show: false },
  ];

  // ─── Table ───────────────────────────────────────────────
  tableHeaders = [
    { title: "CONTRACT NO", type: "data", id: "ContractNo" },
    { title: "NAME AS IN NRIC/PASSPORT", type: "data", id: "Name" },
    {
      title: "NRIC NO/RCB NO/PASSPORT NO",
      type: "data",
      id: "IcNo",
      classes: "max-w-64",
    },
    { title: "REGISTER NUMBER", type: "data", id: "REGISTERNO" },
    {
      title: "MODEL",
      type: "data",
      id: "ModelInterested",
      classes: "max-w-64",
    },
    { title: "ACTION", type: "edit", id: "Edit" },
  ];

  constructor(
    private customerSearchService: CustomerSearchService,
    private notifier: NotifierService,
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────

  ngOnInit(): void {
    this.setModelList();
    this.getActiveUsers();
  }

  // ─── Data Loading ────────────────────────────────────────

  private setModelList(): void {
    this.customerSearchService.getModelDrpList().subscribe(
      (response) => {
        const models = response?.body;
        if (!models || !Array.isArray(models)) return;
        this.fullModelsData = models.map((model: any) => ({
          Type: model.__type,
          Value: model.Value,
          Name: model.Name,
          Description: model.Description,
          Status: model.Status,
        }));
        this.fillModelForm();
      },
      (error) => console.log("Error fetching model list:", error),
    );
  }

  private getActiveUsers(): void {
    this.customerSearchService.getAllActiveUsers().subscribe({
      next: (response) => {
        this.fullActiveUsers = response.body;
        this.getField("SalemansCode").options = response.body.map(
          (item: any) => ({
            display: item.display_name,
            value: item.user_name,
          }),
        );
      },
      error: (error) => console.log(error),
    });
  }

  private fillModelForm(): void {
    this.modelForm = [
      {
        type: "select",
        id: "model",
        label: "Model",
        options: this.fullModelsData.map((model) => ({
          display: model.Name,
          value: model.Value,
        })),
      },
    ];
  }

  // ─── Form Actions ────────────────────────────────────────

  updateFields(event: any): void {
    this.searchFields = event.fields;
    if (event.updatedValue == 16) {
      const selectedSalesman = this.fullActiveUsers.find(
        (item: any) =>
          item.user_name == this.getField("display_name").value,
      );
      this.getField("SalemansID").value = selectedSalesman.user_id;
    }
  }

  updateModelFields(fields: FormField[]): void {
    const modelField = fields.find((field) => field.id === "model");
    if (modelField) {
      this.selectedModelId = modelField.value;
    }
  }

  // ─── Search ──────────────────────────────────────────────

  onSearch(event: any): void {
    const searchType = event.fieldId;
    const searchText = this.getField(event.fieldId).value;

    if (!searchType || !searchText) return;

    this.customerSearchService
      .findContract(searchText, searchType)
      .subscribe({
        next: (response) => {
          if (response.body.length == 0) {
            this.notifier.notify("error", "No Buyers List found");
            this.lockUpdate = true;
            this.lockPrint = true;
            return;
          }

          const contractData = response.body[0];
          const dateFields = ["Dob", "REGISTRATIONDATE"];
          const formatDate = (dateStr: string): string => {
            if (!dateStr) return dateStr;
            const [day, month, year] = dateStr.split("/");
            return `${year}-${month}-${day}`;
          };

          this.searchFields = this.searchFields.map((field: any) => {
            if (field.id === "SalemansCode") {
              const match = this.fullActiveUsers.find(
                (user: any) =>
                  user.user_id === contractData["SalemansID"],
              );
              return { ...field, value: match?.user_name ?? null };
            }
            if (dateFields.includes(field.id)) {
              return {
                ...field,
                value: formatDate(contractData[field.id]),
              };
            }
            return {
              ...field,
              value: contractData[field.id] ?? null,
            };
          });

          this.lockUpdate = false;
          this.lockPrint =
            !!this.getField("ContractNo").value === false;

          const tableKeys = [
            "ContractNo",
            "Name",
            "IcNo",
            "REGISTERNO",
            "ModelInterested",
            "Edit",
          ];
          this.tableData = [
            Object.fromEntries(
              tableKeys.map((key) => [
                key,
                contractData[key] ?? null,
              ]),
            ),
          ];
        },
        error: (error) => console.log(error),
      });
  }

  // ─── Update ──────────────────────────────────────────────

  update(): void {
    const dateFields = ["Dob", "REGISTRATIONDATE"];
    const formatDateBack = (dateStr: string): string => {
      if (!dateStr) return dateStr;
      const [year, month, day] = dateStr.split("-");
      return `${day}/${month}/${year}`;
    };

    const payload = Object.fromEntries(
      this.searchFields.map((field: any) => [
        field.id,
        dateFields.includes(field.id)
          ? formatDateBack(field.value)
          : field.value,
      ]),
    );

    payload["Model"] = payload["ModelInterested"];
    payload["ModelInterested"] = "";
    payload["user_owner"] = "";
    payload["REGISTRATIONDATE"] = new Date().toLocaleDateString(
      "en-GB",
    );
    payload["OfficeNo"] = payload["OfficeNo"] ?? "";

    this.customerSearchService
      .updateSalesContract(payload)
      .subscribe({
        next: () =>
          this.notifier.notify(
            "success",
            "Buyers detail updated successfully",
          ),
        error: (error) => console.log(error),
      });
  }

  // ─── Print & Download ────────────────────────────────────

  print(): void {
    const contractNo = this.getField("ContractNo").value;
    this.customerSearchService.print(contractNo).subscribe(
      (response) => window.open(response.body.Name),
      (error) => console.log(error),
    );
  }

  onDownloadCustomerList(): void {
    this.customerSearchService
      .exportCustomer(this.selectedModelId)
      .subscribe(
        (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "customer.xlsx";
          a.click();
          window.URL.revokeObjectURL(url);
          this.notifier.notify(
            "success",
            "Customer list downloaded successfully.",
          );
        },
        (error) =>
          console.error("Error downloading customer list:", error),
      );
  }

  // ─── Helpers ─────────────────────────────────────────────

  getField(id: string): FormField {
    return (
      this.searchFields.find((f) => f.id === id) || {
        id: "",
        type: "text",
        label: "",
        value: undefined,
      }
    );
  }
}
